import type { SupSingleVaultV3, Networkish } from "@/types";
import type { BigNumberish } from "@ethersproject/bignumber";
import type { ResponseVaults } from "@/hooks/data/useMapVaults";
import type { Strategies } from "@/../config/swr.config";

import useApi from "@/hooks/api/useApi";
import useWeb3UserAddress from "@/hooks/web3/useWeb3UserAddress";
import { processVault } from "@/hooks/data/useMapVaults";

interface ResponseSuperPosition {
  chain_id: Networkish;
  is_erc20: boolean;
  superposition_balance: BigNumberish;
  superposition_id: string;
  superposition_usd_value: number;
  vault: ResponseVaults;
}

interface ResponseSuperpositionBalances {
  portfolio_value: number;
  superpositions: ResponseSuperPosition[] | null;
}

export interface SupWeb3VaultBalance {
  chain_id: Networkish;
  is_erc20: boolean;
  superposition_balance: BigNumberish;
  superposition_id: string;
  superposition_usd_value: number;
  vault: SupSingleVaultV3;
}

export interface SupWeb3SuperpositionsData {
  superPositions: SupWeb3VaultBalance[];
  portfolioValue: number;
}

/**
 * @package api:go
 * @link https://superform-xyz.postman.co/workspace/Superform~fe57d318-f198-4f97-9c50-0b2a5a78dbac/request/3472327-fa88bc58-fe7f-4a87-8d7d-1f0516bf41ed?ctx=documentation
 * @param {Strategies} strategy
 * @param {boolean} enabled
 * @param {boolean} fetchErc20s
 */
export default function useTokenSuperpositionsBalances(strategy: Strategies = "fully-cached", enabled: boolean = true, fetchErc20s: boolean = true) {
  const userAddress = useWeb3UserAddress();

  const { data, isLoading, isError, mutate } = useApi<SupWeb3SuperpositionsData, ResponseSuperpositionBalances>(
    {
      path: `/token/superpositions/balances/${userAddress}/`,
      api: "v2",
      params: {
        fetch_erc20s: fetchErc20s,
      },
    },
    {
      enabled: !!userAddress && enabled,
      strategy,
      select: data => {
        const rawData = data.superpositions || [];
        const mappedSuperPositions = rawData.map((singleBalance): SupWeb3VaultBalance => {
          return {
            ...singleBalance,
            vault: processVault(singleBalance.vault),
          } as SupWeb3VaultBalance;
        }) as SupWeb3VaultBalance[];
        return {
          superPositions: mappedSuperPositions,
          portfolioValue: data.portfolio_value,
        };
      },
    }
  );

  return {
    data,
    isLoading,
    isError,
    mutate,
  };
}
